// Loader entrypoint. Wires identity + consent + context + collectors +
// transport, replaces the stub queue with a real dispatcher, then replays
// whatever the host page queued before we loaded.
//
// Public API (called via `amb('verb', ...args)` so it works identically
// before and after the loader resolves):
//
//   amb('init',        { appId, environment, release, endpoint, ... })
//   amb('setUser',     'user-123')                  | { id, traits? }
//   amb('setRoute',    '/checkout/payment')
//   amb('event',        'add_to_cart', { dims?, metrics?, tags? })
//   amb('consent',     'granted' | 'denied')
//   amb('flush')
//
// Any verb queued before init() is buffered until init resolves the config.

import { safe, safely, now, uuid } from './safe.js';
import { EVENT_TYPES } from './schema.js';
import { initIdentity, getIdentity, setUser as setUserId, touchSession } from './identity.js';
import { initConsent, setConsent, onConsentChange, shouldEmit, getConsent } from './consent.js';
import { initContext, snapshot } from './context.js';
import { initTransport, enqueue, flush, setEnabled, armLifecycleFlush } from './transport.js';
import { initConfig, isKilled, shouldSample } from './config.js';
import { initVitals } from './collectors/vitals.js';
import { initNavigation } from './collectors/navigation.js';
import { initResources } from './collectors/resources.js';
import { initLongTasks } from './collectors/longtasks.js';
import { initErrors } from './collectors/errors.js';
import { initSpa, emitPageView } from './collectors/spa.js';
import { initReplay } from './collectors/replay.js';
import { initHeatmap } from './collectors/heatmap.js';

const GLOBAL = 'amb';
// The loader's own CSP nonce, captured while document.currentScript is still
// valid (synchronous bundle eval). Reused on the injected rrweb script so
// session replay keeps working under a nonce-based script-src CSP.
const SELF_NONCE = safely(() => (document.currentScript && document.currentScript.nonce) || '') || '';
let _state = { initialized: false, routeName: '' };

(function bootstrap() {
  const existing = window[GLOBAL];
  const queue = (existing && existing.q) || [];
  const earlyErrors = (existing && existing.eq) || [];
  const stubCfg = (existing && existing.cfg) || {};

  // Replace the stub with the real dispatcher. We keep the stub's queue
  // visible via .q for late-arriving calls during init.
  const dispatcher = function () {
    const args = [].slice.call(arguments);
    if (!_state.initialized && args[0] !== 'init') {
      queue.push(args);
      return;
    }
    dispatch(args);
  };
  dispatcher.q = queue;
  dispatcher.eq = earlyErrors;
  dispatcher.l = (existing && existing.l) || +new Date();
  dispatcher.v = '__SDK_VERSION__';
  dispatcher._stubCfg = stubCfg;
  window[GLOBAL] = dispatcher;

  // Replay only the init call first; everything else waits for init.
  const initIdx = queue.findIndex((a) => a[0] === 'init');
  if (initIdx >= 0) {
    const initArgs = queue.splice(initIdx, 1)[0];
    dispatch(initArgs);
  } else if (stubCfg && stubCfg.appId) {
    // Author configured via data-* attributes on the stub <script>; no JS
    // init() call needed.
    dispatch(['init', stubCfg]);
  }

  // Now replay queued, non-init calls.
  while (queue.length) {
    const a = queue.shift();
    if (a[0] === 'init') continue;
    try { dispatch(a); } catch (_) { /* silent */ }
  }

  // Drain the stub's early-error queue into the errors collector. We do this
  // even before init resolves so we don't lose them; the errors collector
  // gates emission on shouldSample once active.
  // (Actual drain happens inside initErrors below — we just keep the array.)
})();

function dispatch(args) {
  const verb = args[0];
  switch (verb) {
    case 'init':     return safe(() => doInit(args[1] || {}))();
    case 'setUser':  return safe(() => doSetUser(args[1]))();
    case 'setRoute': return safe(() => doSetRoute(args[1]))();
    case 'event':    return safe(() => doEvent(args[1], args[2]))();
    case 'consent':  return safe(() => setConsent(args[1]))();
    case 'flush':    return safe(() => flush('manual'))();
  }
}

function doInit(cfg) {
  if (_state.initialized) return;
  const stub = (window[GLOBAL] && window[GLOBAL]._stubCfg) || {};
  cfg = mergeCfg(stub, cfg);
  if (!cfg.appId) return;                 // refuse to start without an app

  const identity = initIdentity({
    sessionInactivityMs: cfg.sessionInactivityMs,
    cookieDomain: cfg.cookieDomain,
    cookieSameSite: cfg.cookieSameSite,
    cookieSecure: cfg.cookieSecure
  });

  initConsent({
    defaultState: cfg.defaultConsent || 'pending',
    honorDnt: cfg.honorDnt !== false,
    honorGpc: cfg.honorGpc !== false
  });

  initContext({
    appId: cfg.appId,
    environment: cfg.environment || 'production',
    release: cfg.release || ''
  });

  // Point the remote-config loader (kill-switch, sampling, and the per-app
  // implicitConsent / honorDoNotTrack flags) at GET /v1/config — derived from
  // the beacon endpoint unless the host passed an explicit configUrl.
  const configUrl = cfg.configUrl || deriveConfigUrl(cfg.endpoint, cfg.appId, cfg.environment);
  initConfig(configUrl ? Object.assign({}, cfg, { configUrl: configUrl }) : cfg, identity.anonymousId);
  if (isKilled()) {                       // remote kill-switch
    setEnabled(false);
    return;
  }

  initTransport({
    endpoint: cfg.endpoint || '/_amb/ingest',
    flushIntervalMs: cfg.flushIntervalMs || 5000,
    flushMaxEvents: cfg.flushMaxEvents || 30,
    maxBufferEvents: cfg.maxBufferEvents || 250,
    appId: cfg.appId,
    token: cfg.token || null
  }, () => buildCtx());

  const viewIdProvider = () => getIdentity().viewId;
  // Web vitals and navigation timing describe the document navigation they
  // were measured on, not the SPA route active when they finally report
  // (LCP/CLS/INP often report at tab-hide, long after the route — and hence
  // viewId — moved on, e.g. via an early replaceState). Pin them to the
  // document's view id so they join with that view's page_view/FCP.
  const documentViewIdProvider = () => getIdentity().documentViewId;

  initVitals(documentViewIdProvider, emit);
  initNavigation(documentViewIdProvider, emit);
  if (shouldSample(EVENT_TYPES.RESOURCE_TIMING)) {
    initResources(viewIdProvider, emit, cfg.endpoint || '/_amb/ingest');
  }
  initLongTasks(viewIdProvider, emit);
  initErrors(viewIdProvider, emit, (window[GLOBAL] && window[GLOBAL].eq) || []);
  initSpa(emit, () => _state.routeName);

  // Arm the terminal flushes only now — after initVitals — so web-vitals'
  // own 'visibilitychange' reporters run before the hidden-flush and a
  // bounce's LCP/CLS/INP ride the final beacon instead of dying in the
  // buffer (see armLifecycleFlush).
  armLifecycleFlush();

  // Session replay (rrweb) — lazy, gated, opt-in. initReplay only registers
  // config/consent listeners here; rrweb is fetched later, and only for the
  // sessions actually sampled for recording.
  initReplay({
    endpoint: cfg.endpoint || '/_amb/ingest',
    token: cfg.token || null,
    appId: cfg.appId,
    environment: cfg.environment || 'production',
    replayScriptUrl: cfg.replayScriptUrl || '',
    privacy: cfg.replay || {},
    nonce: SELF_NONCE
  });

  // Heatmaps (clicks + scroll depth + masked DOM snapshots) — same lazy,
  // gated, opt-in story as replay; reuses replay's privacy overrides so host
  // masking config covers both features.
  initHeatmap({
    endpoint: cfg.endpoint || '/_amb/ingest',
    token: cfg.token || null,
    appId: cfg.appId,
    environment: cfg.environment || 'production',
    replayScriptUrl: cfg.replayScriptUrl || '',
    privacy: cfg.replay || {},
    nonce: SELF_NONCE,
    getRouteName: () => _state.routeName
  }, emit);

  // Initial page view. We do this last so identity + transport are ready.
  emitPageView(emit, () => _state.routeName, { navigationType: 'navigate' });

  // When the consent gate opens — an explicit grant, or an implicitConsent /
  // honorDoNotTrack option arriving from /v1/config — flush what we buffered.
  onConsentChange(() => {
    if (shouldEmit()) flush('consent');
  });

  _state.initialized = true;
}

function doSetUser(arg) {
  if (typeof arg === 'string') setUserId(arg);
  else if (arg && arg.id) setUserId(arg.id);
  touchSession();
}

function doSetRoute(route) {
  _state.routeName = String(route || '');
  touchSession();
}

// Custom-event `dim`/`met`/`tag` come straight from host code. The wire
// contract types `met` as <string,number>, `dim` as <string,string> and
// `tag` as string[]; a stray null, NaN, or wrong-typed value would make the
// receiver reject the whole batch as malformed JSON. Coerce/drop here so one
// bad property can't sink the beacon.
function cleanMetrics(obj) {
  const out = {};
  if (obj) for (const k of Object.keys(obj)) {
    const v = obj[k];
    if (typeof v === 'number' && Number.isFinite(v)) out[k] = v;
  }
  return out;
}

function cleanDimensions(obj) {
  const out = {};
  if (obj) for (const k of Object.keys(obj)) {
    const v = obj[k];
    if (v != null) out[k] = String(v);
  }
  return out;
}

function cleanTags(arr) {
  return Array.isArray(arr) ? arr.filter((t) => t != null).map(String) : undefined;
}

function doEvent(name, props) {
  if (!shouldSample(EVENT_TYPES.CUSTOM_ACTION)) return;
  const id = getIdentity();
  if (!id) return;
  emit({
    id: id.viewId + ':ev:' + uuid(),
    t: now(),
    tp: EVENT_TYPES.CUSTOM_ACTION,
    n: String(name || 'custom'),
    mv: (props && Number.isFinite(props.value)) ? props.value : 0,
    mu: (props && props.unit) || '',
    mr: '',
    mnt: '',
    dim: cleanDimensions(props && props.dims),
    met: cleanMetrics(props && props.metrics),
    tag: cleanTags(props && props.tags)
  });
  touchSession();
}

function emit(event) {
  if (!shouldEmit() && event.tp !== EVENT_TYPES.ERROR) {
    // While consent is pending, hold vitals/nav/resources/longtasks back.
    // Errors we still buffer because the consent flip might be imminent and
    // we'd rather over-collect-then-drop than miss the crash that motivated
    // the flip. The transport gate gives the final say.
  }
  enqueue(event);
}

function buildCtx() {
  const id = getIdentity();
  const ctx = snapshot(_state.routeName, id ? id.viewId : '');
  if (id) {
    ctx.anonymousId = id.anonymousId;
    ctx.userId = id.userId;
    ctx.sessionId = id.sessionId;
    ctx.viewId = id.viewId;
    ctx.sessionStartedAt = id.sessionStartedAt;
    ctx.isNewSession = id.isNewSession;
    ctx.isNewUser = id.isNewUser;
  }
  ctx.consentState = getConsent();
  return ctx;
}

function mergeCfg(stub, cfg) {
  return {
    appId:       cfg.appId       || stub.appId,
    environment: cfg.environment || stub.environment,
    release:     cfg.release     || stub.release,
    endpoint:    cfg.endpoint    || stub.endpoint,
    token:       cfg.token       || stub.token,
    ...cfg
  };
}

// Derive the GET /v1/config URL from the beacon endpoint
// (…/v1/events → …/v1/config?app=&env=). Returns null when the endpoint isn't
// a /v1/events URL, leaving remote config inert — the SDK then runs on its
// compiled defaults (and its init-time consent settings).
function deriveConfigUrl(endpoint, appId, environment) {
  if (typeof endpoint !== 'string') return null;
  const i = endpoint.indexOf('/v1/events');
  if (i < 0) return null;
  return endpoint.slice(0, i) + '/v1/config?app=' + encodeURIComponent(appId) +
    '&env=' + encodeURIComponent(environment || 'production');
}
